import { useEffect, useState } from 'react'
import { Link2, Loader2, Search } from 'lucide-react'
import { SectionCard } from '@/components/ui/SectionCard'
import { cn } from '@/lib/utils/helpers'
import {
  showApiError,
  showSuccess,
  showValidationMessage,
  showWarning,
} from '@/lib/utils/notifications'
import companyFormStyles from '@/features/company/styles/companyAdd.module.css'
import modalStyles from '@/features/application/styles/modals.module.css'
import { ResidentDialogShell } from '../dialogs/ResidentDialogShell'
import { YardiResidentSyncPanel } from './YardiResidentSyncPanel'
import {
  confirmManualYardiResidentMapping,
  fetchYardiPropertyContext,
  validateYardiResidentTCode,
} from '../../services/yardiResidentService'
import type { YardiResidentRecord, YardiTCodeValidationResponse } from '../../types/yardiResidentSync'
import type { ResidentPmsIntegrationTabProps } from '../../types'

function validationRecord(result: YardiTCodeValidationResponse | null): YardiResidentRecord {
  if (!result) return {}
  return result.yardi_resident ?? result.data ?? result.resident ?? result
}

function formatPmsDisplayMessage(message?: string | null): string {
  return String(message || '')
    .trim()
    .replace(/\btcode\b/gi, 'TCode')
}

export function ResidentPmsIntegrationTab({
  propertyId,
  residentId,
  currentTCode,
  onUpdated,
}: ResidentPmsIntegrationTabProps) {
  const [companyId, setCompanyId] = useState<string | number>('')
  const [propertyCode, setPropertyCode] = useState('')
  const [propertyName, setPropertyName] = useState('')
  const [contextLoading, setContextLoading] = useState(true)
  const [tCode, setTCode] = useState('')
  const [validating, setValidating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [validation, setValidation] = useState<YardiTCodeValidationResponse | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    setContextLoading(true)
    void fetchYardiPropertyContext(propertyId)
      .then((context) => {
        if (cancelled) return
        setCompanyId(context.companyId)
        setPropertyCode(context.propertyCode)
        setPropertyName(context.propertyName)
      })
      .catch(showApiError)
      .finally(() => {
        if (!cancelled) setContextLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [propertyId])

  const closeModal = () => {
    setModalOpen(false)
  }

  const validate = async () => {
    const normalizedTCode = tCode.trim()
    if (!normalizedTCode) {
      showWarning('Enter a TCode to validate.')
      return
    }
    setValidating(true)
    setValidation(null)
    setModalOpen(false)
    try {
      const freshContext = await fetchYardiPropertyContext(propertyId)
      setCompanyId(freshContext.companyId)
      setPropertyCode(freshContext.propertyCode)
      setPropertyName(freshContext.propertyName)
      if (!freshContext.companyId || !freshContext.propertyCode) {
        showWarning('Property PMS configuration is not available.')
        return
      }
      const result = await validateYardiResidentTCode({
        property_id: propertyId,
        company_id: freshContext.companyId,
        property_code: freshContext.propertyCode,
        resident_default_id: residentId,
        tCode: normalizedTCode,
      })
      const status = String(result?.status ?? '').toUpperCase()
      const displayMessage =
        formatPmsDisplayMessage(result?.message) ||
        (status === 'NOT_FOUND' ? 'TCode not found in Yardi data.' : status.replace(/_/g, ' '))

      // Same fast validation popup used on save / required-field flows.
      if (status !== 'READY_TO_MAP' && status !== 'ALREADY_MAPPED') {
        setValidation(null)
        showValidationMessage(
          displayMessage || 'TCode not found in Yardi data.',
          'Resident TCode Validation',
        )
        return
      }

      setValidation(result)
      setModalOpen(true)
    } catch (error) {
      showApiError(error)
    } finally {
      setValidating(false)
    }
  }

  const save = async () => {
    if (validation?.status !== 'READY_TO_MAP') return
    setSaving(true)
    try {
      const freshContext = await fetchYardiPropertyContext(propertyId)
      setCompanyId(freshContext.companyId)
      setPropertyCode(freshContext.propertyCode)
      setPropertyName(freshContext.propertyName)
      if (!freshContext.companyId || !freshContext.propertyCode) {
        showWarning('Property PMS configuration is not available.')
        return
      }
      const result = await confirmManualYardiResidentMapping(
        {
          property_id: propertyId,
          company_id: freshContext.companyId,
          property_code: freshContext.propertyCode,
          resident_default_id: residentId,
        },
        tCode.trim(),
        validationRecord(validation),
      )
      const resultObject =
        result && typeof result === 'object' ? (result as Record<string, unknown>) : {}
      const mapped = Array.isArray(resultObject.mapped) ? resultObject.mapped : []
      const skipped = Array.isArray(resultObject.skipped) ? resultObject.skipped : []
      if (!mapped.length) {
        const first = skipped[0] as Record<string, unknown> | undefined
        showWarning(String(first?.reason ?? first?.message ?? 'Unable to save mapping.'))
        return
      }
      showSuccess('Resident Yardi mapping updated successfully.')
      setTCode('')
      setValidation(null)
      setModalOpen(false)
      await onUpdated()
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  const record = validationRecord(validation)
  const status = String(validation?.status ?? '').toUpperCase()
  const displayMessage = formatPmsDisplayMessage(validation?.message)
  const statusPanelClass =
    status === 'READY_TO_MAP'
      ? 'border-[#9ed4b8] bg-[#e8f7ef]'
      : 'border-[#f5d98c] bg-[#fff8e6]'

  const residentName =
    record.name ||
    `${String(record.first_name ?? record.firstName ?? '')} ${String(record.last_name ?? record.lastName ?? '')}`.trim()

  const factRows: Array<[string, unknown]> = [
    ['TCode', record.tCode ?? tCode],
    ['PCode', record.pCode],
    ['Name', residentName],
    ['Email', record.email],
    ['Unit', record.unit ?? record.unit_number ?? record.unitCode],
    ['Lease Start', record.lease_start_date ?? record.leaseFromDate],
    ['Move In', record.move_in_date ?? record.moveInDate],
  ]

  return (
    <div className="space-y-5" data-testid="resident-pms-integration-tab">
      <SectionCard icon={Link2} title="PMS Integration">
        {contextLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading Yardi property configuration…
          </div>
        ) : (
          <YardiResidentSyncPanel
            propertyId={propertyId}
            companyId={companyId}
            propertyCode={propertyCode}
            propertyName={propertyName}
            residentId={residentId}
            onConfirmed={onUpdated}
            refreshContextBeforeRequest
          />
        )}
      </SectionCard>

      <SectionCard icon={Link2} title="Update Residents Mapping">
        <div className="space-y-4">
          {currentTCode ? (
            <p className="text-sm text-slate-600">
              Current TCode: <span className="font-semibold text-slate-900">{currentTCode}</span>
            </p>
          ) : null}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <label className="block w-full max-w-[320px] text-sm font-medium text-slate-700">
              Resident TCode
              <input
                value={tCode}
                onChange={(event) => {
                  setTCode(event.target.value)
                  setValidation(null)
                  setModalOpen(false)
                }}
                placeholder="Enter Yardi TCode"
                className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                data-testid="resident-pms-tcode-input"
              />
            </label>
            <button
              type="button"
              onClick={() => void validate()}
              disabled={validating || contextLoading || !tCode.trim()}
              className={cn(companyFormStyles.btnPrimary, 'h-10 min-w-[120px] gap-2')}
              data-testid="resident-pms-tcode-validate-button"
            >
              {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Validate
            </button>
          </div>
        </div>
      </SectionCard>

      <ResidentDialogShell
        isOpen={modalOpen && !!validation}
        title="Resident TCode Validation"
        headerVariant="centeredTitle"
        onClose={closeModal}
        maxWidth="max-w-xl"
        panelDataTestId="resident-pms-tcode-result"
        footerAlign="end"
        footer={
          <div
            className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end"
            data-testid="resident-pms-tcode-result-footer"
          >
            <button
              type="button"
              onClick={closeModal}
              className={modalStyles.btnSaveChangesSecondary}
              data-testid="resident-pms-tcode-result-close-button"
            >
              Close
            </button>
            {status === 'READY_TO_MAP' ? (
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving}
                className={cn(modalStyles.btnSaveChangesPrimary, 'inline-flex items-center justify-center gap-2')}
                data-testid="resident-pms-tcode-save-button"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save
              </button>
            ) : null}
          </div>
        }
      >
        <div className="space-y-4" data-testid="resident-pms-tcode-result-body">
          {displayMessage ? (
            <p
              className={cn(
                'm-0 rounded-lg border px-3.5 py-2.5 text-center text-sm font-semibold text-[#232946]',
                statusPanelClass,
              )}
            >
              {displayMessage}
            </p>
          ) : null}

          <div>
            <h3 className="m-0 mb-3.5 text-base font-bold text-[#232946]">Yardi Resident</h3>
            <dl className="m-0 grid gap-2.5">
              {factRows.map(([label, value]) => (
                <div key={label} className="flex items-start gap-2 text-sm">
                  <dt className="m-0 min-w-[108px] font-bold text-[#232946]">{label} :</dt>
                  <dd className="m-0 min-w-0 flex-1 break-words text-[#51607a]">
                    {value != null && String(value).trim() ? String(value) : '--'}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </ResidentDialogShell>
    </div>
  )
}
